import { Info } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@axilon-apps/libs-ui-shadcn'

import { PasswordRequirement } from '../../../../types/password'
import { DataTestId } from '../../../../constants/data-test-id'

type Placement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end'

interface Props {
  requirements: PasswordRequirement[]
  placement?: Placement
}

const placementToSideAlign = (
  placement: Placement
): { side: 'top' | 'bottom' | 'left' | 'right'; align: 'start' | 'center' | 'end' } => {
  const [side, alignSuffix] = placement.split('-') as [
    'top' | 'bottom' | 'left' | 'right',
    'start' | 'end' | undefined
  ]
  return { side, align: alignSuffix ?? 'center' }
}

const PasswordRequirementsTooltip = ({ requirements, placement = 'right-start' }: Props) => {
  const { side, align } = placementToSideAlign(placement)

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="-ml-1 inline-flex">
            <Info
              size={20}
              className="text-muted-foreground cursor-help"
              data-testid={DataTestId.PasswordRequirementsToggle}
            />
          </span>
        </TooltipTrigger>
        <TooltipContent
          variant="surface"
          side={side}
          align={align}
          aria-label="Password requirements"
        >
          <div className="flex flex-col gap-1 p-2">
            {requirements.map((requirement, index) => (
              <p
                key={index}
                className={
                  'flex items-center gap-2 text-xs ' +
                  (requirement.isMet ? 'text-st-run' : 'text-muted-foreground')
                }
                data-testid={DataTestId.PasswordRequirement}
              >
                {requirement.isMet ? '✓' : '○'} {requirement.label}
              </p>
            ))}
            <p
              className="border-border text-muted-foreground mt-2 border-t pt-2 text-xs italic"
              data-testid={DataTestId.PasswordRequirementsNote}
            >
              Combine requirements to achieve &apos;Strong - Acceptable&apos; or above.
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default PasswordRequirementsTooltip
